import type { FC } from "react";
import { useState } from "react";

import { toast } from "@jti/core-components/components/Toaster";
import { truncateText } from "@jti/fe-common";
import type { Product } from "@jti/magento";
import { getCurrentMagentoClient } from "@jti/magento";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { fetchCartItems, removeItem } from "@jti/store/slices/cartSlice";
import { QuantityInput, Tooltip, useTranslations } from "@jti/ui";

import type { SelectedFreeGifts, SetSelectedFreeGiftQuantity } from "@checkout/hooks/useFreeGifts";
import { getGiftItemsFromCart } from "@checkout/utils/getGiftItemsFromCart";

import styles from "./FreeGift.module.scss";

const maxTooltipLength = 130;

export type FreeGiftProps = {
  name: string;
  parentItemId: string;
  giftSku: string;
  parentItemSku: string;
  image?: {
    url: string;
    label: string;
  };
  hasReachedLimit: boolean;
  selectedFreeGifts: SelectedFreeGifts;
  setSelectedFreeGiftQuantity: SetSelectedFreeGiftQuantity;
  tooltip?: string;
  quantityAddedToCart: number;
};

export const FreeGift: FC<FreeGiftProps> = ({
  name,
  image,
  parentItemId,
  parentItemSku,
  giftSku,
  hasReachedLimit,
  selectedFreeGifts,
  quantityAddedToCart,
  setSelectedFreeGiftQuantity,
  tooltip,
}) => {
  const dispatch = useDispatch();
  const { __ } = useTranslations();

  const [isPending, setIsPending] = useState(false);
  const cartId = useSelector((state) => state.cart.id) ?? "";
  const productsInCart = useSelector((state) => state.cart.items);

  const count = selectedFreeGifts[parentItemId]?.[giftSku] || 0;

  const removeGiftItems = async (items: Product[], cartId: string) => {
    const removalPromises = items.filter((item) => item?.id).map((item) => dispatch(removeItem({ cartId, item })));

    await Promise.all(removalPromises);
  };

  const handleGiftCountChange = async (upcomingValue: number) => {
    if (upcomingValue === count) return;

    setIsPending(true);
    const client = getCurrentMagentoClient();
    const giftItems = getGiftItemsFromCart(productsInCart, giftSku, parentItemId);

    try {
      await removeGiftItems(giftItems, cartId);

      if (upcomingValue > 0) {
        await client.addProductsToCart(cartId, [
          {
            productSku: parentItemSku,
            giftSku,
            quantity: upcomingValue,
            parentGiftId: parentItemId,
          },
        ]);
      }

      await dispatch(fetchCartItems(cartId));
      setSelectedFreeGiftQuantity({ parentGiftId: parentItemId, giftSku, quantity: upcomingValue });
    } catch (err) {
      toast.error(__("cart.checkout.free_gifts.error"));
      console.error("Error updating free gift quantity:", err);
      newrelic.noticeError(new Error(`Error updating free gift quantity: ${err}`));
    } finally {
      setIsPending(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.description}>
          {image && <img data-testid="freeGiftImage" src={image.url} alt={image.label} className={styles.image} />}
          <span data-testid="freeGiftName" className={styles.name}>
            {name}
          </span>
          {tooltip && (
            <div className={styles.tooltip}>
              <Tooltip content={<span>{truncateText(tooltip, maxTooltipLength)}</span>} size="small" withCloseIcon />
            </div>
          )}
        </div>

        <QuantityInput
          value={quantityAddedToCart}
          min={0}
          disabled={isPending}
          disabledAdd={hasReachedLimit}
          onChange={(value) => handleGiftCountChange(value)}
        />
      </div>
    </div>
  );
};
